// src/store/slices/mediaSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { downloadChatMedia } from '../../services/whatsapp';
import { Message } from './messagesSlice';
import { Logger } from '../../utils/logger';

// Types
interface MediaCacheItem {
  url: string;
  type: string;
  mimeType: string;
  timestamp: number;
}

export interface MediaState {
  cache: {
    [messageId: string]: MediaCacheItem;
  };
  previewOpen: boolean;
  previewMessageId: string | null;
  downloading: {
    [messageId: string]: boolean;
  };
  errors: {
    [messageId: string]: string;
  };
}

// Initial state
const initialState: MediaState = {
  cache: {},
  previewOpen: false,
  previewMessageId: null,
  downloading: {},
  errors: {},
};

// Configuration
const MEDIA_CACHE_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours


// In mediaSlice.ts - Add these additional selectors and actions

// Add a custom selector to get media preview URL
// In mediaSlice.ts - Add logging to debug the selector
export const selectMediaPreviewUrl = (state: RootState) => {
  const messageId = state.whatsapp.media.previewMessageId;
  const url = messageId ? state.whatsapp.media.cache[messageId]?.url : null;

  return url;
};

// Add a selector to get media type for preview
export const selectMediaPreviewType = (state: RootState) => {
  const messageId = state.whatsapp.media.previewMessageId;
  return messageId ? state.whatsapp.media.cache[messageId]?.type : null;
};

// Add a selector to get media mime type for preview
export const selectMediaPreviewMimeType = (state: RootState) => {
  const messageId = state.whatsapp.media.previewMessageId;
  return messageId ? state.whatsapp.media.cache[messageId]?.mimeType : null;
};

// Add a helper action to handle downloading and caching in one step
export const downloadAndCacheMedia = createAsyncThunk(
  'media/downloadAndCacheMedia',
  async (message: Message, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(downloadMedia(message)).unwrap();
      dispatch(cacheMedia({
        messageId: message._id,
        url: result.url,
        type: result.type,
        mimeType: result.mimeType
      }));
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Create the downloadMedia thunk
export const downloadMedia = createAsyncThunk(
  'media/downloadMedia',
  async (message: Message, { rejectWithValue }) => {
    try {
      Logger.log("Downloading media for message:", message._id);
      
      if (!message?.message?.id) {
        return rejectWithValue('No media ID available');
      }
      
      let mediaId = message.message.id;
      let formdata = new FormData();
      formdata.append("mediaId", mediaId);
      
      const response = await downloadChatMedia(formdata);
      
      if (response.data) {
        const blob = new Blob([response.data], {
          type: message.messageType === "document"
            ? "application/pdf"
            : message.message.mime_type
        });
        
        const url = URL.createObjectURL(blob);
        
        Logger.log("Media downloaded successfully", { 
          messageId: message._id, 
          type: message.messageType 
        });
        
        return {
          messageId: message._id,
          url,
          type: message.messageType || 'unknown',
          mimeType: message.message.mime_type || 'application/octet-stream'
        };
      }
      
      return rejectWithValue('Failed to download media');
    } catch (error: any) {
      Logger.log("Media download error:", error);
      return rejectWithValue(error.message || 'Media download failed');
    }
  }
);

// Create the slice
const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    cacheMedia: (state, action: PayloadAction<{
      messageId: string;
      url: string;
      type: string;
      mimeType: string;
    }>) => {
      const { messageId, url, type, mimeType } = action.payload;
      
      state.cache[messageId] = {
        url,
        type,
        mimeType,
        timestamp: Date.now(),
      };
    },
    
    removeExpiredCache: (state) => {
      const now = Date.now();
      
      // Filter out expired items
      Object.keys(state.cache).forEach(messageId => {
        if (now - state.cache[messageId].timestamp > MEDIA_CACHE_MAX_AGE) {
          delete state.cache[messageId];
        }
      });
    },
    
    clearCache: (state) => {
      state.cache = {};
    },
    
   // In mediaSlice.ts
openMediaPreview: (state, action: PayloadAction<string>) => {
  const messageId = action.payload;
  Logger.log("Opening media preview for message:", messageId);
  Logger.log("Available cache keys:", Object.keys(state.cache));
  
  state.previewOpen = true;
  state.previewMessageId = messageId;
  
  // Check if it's in cache
  if (!state.cache[messageId]) {
    Logger.log("WARNING: Opening preview for uncached media:", messageId);
  }
},
    
    closeMediaPreview: (state) => {
      state.previewOpen = false;
      state.previewMessageId = null;
    },
    
    setMediaDownloading: (state, action: PayloadAction<{ 
      messageId: string; 
      downloading: boolean 
    }>) => {
      const { messageId, downloading } = action.payload;
      state.downloading[messageId] = downloading;
    },
    
    setMediaError: (state, action: PayloadAction<{ 
      messageId: string; 
      error: string 
    }>) => {
      const { messageId, error } = action.payload;
      state.errors[messageId] = error;
    },
    
    clearMediaError: (state, action: PayloadAction<string>) => {
      const messageId = action.payload;
      delete state.errors[messageId];
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle downloadMedia
      .addCase(downloadMedia.pending, (state, action) => {
        const messageId = action.meta.arg._id;
        state.downloading[messageId] = true;
        state.errors[messageId] = '';
      })
      .addCase(downloadMedia.fulfilled, (state, action) => {
        const { messageId, url, type, mimeType } = action.payload;
        
        state.downloading[messageId] = false;
        state.cache[messageId] = {
          url,
          type,
          mimeType,
          timestamp: Date.now(),
        };
      })
      .addCase(downloadMedia.rejected, (state, action) => {
        const messageId = action.meta.arg._id;
        state.downloading[messageId] = false;
        state.errors[messageId] = action.payload as string;
      });
  }
});

// Export actions
export const {
  cacheMedia,
  removeExpiredCache,
  clearCache,
  openMediaPreview,
  closeMediaPreview,
  setMediaDownloading,
  setMediaError,
  clearMediaError
} = mediaSlice.actions;

// Export selectors
export const selectMediaCache = (state: RootState) => state.whatsapp.media.cache;
export const selectMediaCacheItem = (messageId: string) => 
  (state: RootState) => state.whatsapp.media.cache[messageId];
export const selectIsMediaCached = (messageId: string) => 
  (state: RootState) => !!state.whatsapp.media.cache[messageId];
export const selectPreviewOpen = (state: RootState) => state.whatsapp.media.previewOpen;
export const selectPreviewMessageId = (state: RootState) => state.whatsapp.media.previewMessageId;
export const selectIsMediaDownloading = (messageId: string) => 
  (state: RootState) => !!state.whatsapp.media.downloading[messageId];
export const selectMediaError = (messageId: string) => 
  (state: RootState) => state.whatsapp.media.errors[messageId] || '';

// Export reducer
export default mediaSlice.reducer;